import React from 'react';
import { NavLink } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Button } from 'components/Button';
import AssignmentDesktop from './desktop/View';
import AssignmentMobile from './mobile/View';
import useWindowWidth from 'components/hooks/useWindowWidth';
import useAssignment from './hooks/useAssignment';
import { TYPE, BankIDDialog } from 'components/bankID/BankID';
import api from 'api/Api';
import { AssignmentStatus } from 'typings/assignment';

interface Props {
    assignmentID: string;
}

type SignTarget =
    | {
          type: 'file';
          uuid: string;
      }
    | {
          type: 'bookingConfirmation';
      };

export default function Assignment({ assignmentID }: Props) {
    const { informationProps, reportProps, filesProps, statsProps } =
        useAssignment(assignmentID);
    const { isMobile } = useWindowWidth();
    const [selectedSignTarget, setSelectedSignTarget] =
        React.useState<SignTarget | null>(null);

    const handleSignFile = (uuid: string) => {
        setSelectedSignTarget({ type: 'file', uuid });
    };

    const handleSignBookingConfirmation = () => {
        setSelectedSignTarget({ type: 'bookingConfirmation' });
    };

    const signEndPoint =
        selectedSignTarget?.type === 'bookingConfirmation'
            ? api.assignment.signBookingConfirmationWithDigitalID(assignmentID)
            : api.files.signFileWithDigitalID(
                  assignmentID,
                  selectedSignTarget?.uuid || ''
              );

    const renderTimesheetButton = () => {
        const { status, report } = informationProps.data;
        const isActive = status === AssignmentStatus.BOOKED || status === AssignmentStatus.TIME_REPORTED;
        const hasTimesheet = !!report?.submittedAt;
        if (isActive && hasTimesheet) {
            return (
                <NavLink exact to={'/uppdrag/' + assignmentID + '/tidrapport'}>
                    <Button
                        variant='outlined'
                        onClick={() => triggerEditTimereportEvent(true)}
                        fullWidth>
                        Visa tidrapport
                    </Button>
                </NavLink>
            );
        } else if (hasTimesheet) {
            return (
                <NavLink exact to={'/uppdrag/' + assignmentID + '/tidrapport'}>
                    <Button
                        variant='outlined'
                        onClick={() => triggerViewTimereportEvent()}
                        fullWidth>
                        Visa tidrapport
                    </Button>
                </NavLink>
            );
        } else if (isActive) {
            return (
                <NavLink exact to={'/uppdrag/' + assignmentID + '/tidrapport'}>
                    <Button
                        variant='primary'
                        onClick={() => triggerEditTimereportEvent(false)}
                        fullWidth>
                        Skicka in tidrapport
                    </Button>
                </NavLink>
            );
        } else {
            return undefined;
        }
    };

    const triggerViewTimereportEvent = () => {
        ReactGA.event({
            category: 'AssignmentPage',
            action: 'View timereport',
        });
    };

    const triggerEditTimereportEvent = (hasTimesheet: boolean) => {
        ReactGA.event({
            category: 'AssignmentPage',
            action: 'Edit timereport',
            label: hasTimesheet ? 'Old' : 'New',
        });
    };

    const renderMobile = () => {
        return (
            <AssignmentMobile
                informationProps={informationProps}
                reportProps={reportProps}
                filesProps={filesProps}
                statsProps={statsProps}
                renderTimesheetButton={renderTimesheetButton}
                onSignFile={handleSignFile}
                onSignBookingConfirmation={handleSignBookingConfirmation}
            />
        );
    };

    const renderDesktop = () => {
        return (
            <AssignmentDesktop
                informationProps={informationProps}
                reportProps={reportProps}
                filesProps={filesProps}
                statsProps={statsProps}
                renderTimesheetButton={renderTimesheetButton}
                onSignFile={handleSignFile}
                onSignBookingConfirmation={handleSignBookingConfirmation}
            />
        );
    };

    return (
        <>
            {!isMobile ? renderDesktop() : renderMobile()}
            <BankIDDialog
                open={!!selectedSignTarget}
                onGoBack={() => setSelectedSignTarget(null)}
                onSuccess={() => {
                    setSelectedSignTarget(null);
                    informationProps.fetch();
                    filesProps.fetchFiles();
                }}
                type={TYPE.SIGN}
                endPoint={signEndPoint}
                title='Skriv under dokument'
                subtitle='Välj identifieringsmetod för att fortsätta.'
                isMobile={isMobile}
            />
        </>
    );
}
